import { useForm } from '@mantine/form'
import { showConfirmSettingModal } from 'App/Map/UserSettingsMenu/Shared/_utils/showConfirmSettingModal'
import { ConfirmationSettingsModal } from 'components/ConfirmationSettingsModal/ConfirmationSettingsModal'
import { ErrorText } from 'components/ErrorText/ErrorText'
import { ModalDrawer } from 'components/ModalDrawer/ModalDrawer'
import { NameWithExternalVendorIcons } from 'components/NameWithExternalVendorIcons/NameWithExternalVendorIcons'
import { NumberInputWithUnit } from 'components/NumberInputWithUnit/NumberInputWithUnit'
import { translate } from 'i18n/i18n'
import { useEffect, useState } from 'react'
import { colors } from 'settings/colors'
import { SharedSettings } from 'settings/SharedSettings'
import { detailsPanelStore } from 'stores/detailsPanelStore/detailsPanelStore'
import { selectedFieldAssetsStore } from 'stores/selectedFieldAssetsStore/selectedFieldAssetsStore'
import { apiFetch } from 'utils/apiFetch'
import type { TPointExternalVendorNames } from 'utils/getPointExternalVendorNames'
import { showNotification } from 'utils/showNotification'
import { convertCurrentFlowRateToGpm } from 'utils/unitConverter/by-domain/flowMeter'
import { unitConverter } from 'utils/unitConverter/unitConverter'
import { zodResolver } from 'utils/zodResolver'
import { z } from 'zod'

export const FlowMeterSettingsModal = ({
  defaultMinFlowRate,
  defaultMaxFlowRate,
  name,
  externalVendorNames,
  handleAfterSave,
}: {
  defaultMinFlowRate: number | ''
  defaultMaxFlowRate: number | ''
  name: string
  externalVendorNames?: TPointExternalVendorNames
  handleAfterSave: (minFlowRate: number | null, maxFlowRate: number | null) => void
}) => {
  const flowMeterLimitsSettingsOpened = detailsPanelStore.useSelector((s) => {
    return s.flowMeterLimitsSettingsOpened
  })

  const { flowMeterDevice: lngLat, property: propertyId } = selectedFieldAssetsStore.useSelector((s) => {
    return {
      flowMeterDevice: s.flowMeterDevice,
      property: s.property,
    }
  })

  const [confirmModalOpened, setConfirmModalOpened] = useState(false)

  const schema = z
    .object({
      minFlowRate: z
        .number({
          error: (issue) =>
            issue.code === 'invalid_type'
              ? translate.phrases.validation('{{label}} is required', {
                  label: translate.phrases.banyanApp('Minimum Flow Rate'),
                })
              : undefined,
        })
        .positive({
          message: translate.phrases.validation('{{label}} must be > {{value}}', {
            label: translate.phrases.banyanApp('Minimum Flow Rate'),
            value: 0,
          }),
        }),
      maxFlowRate: z.number({
        error: (issue) =>
          issue.code === 'invalid_type'
            ? translate.phrases.validation('{{label}} is required', {
                label: translate.phrases.banyanApp('Maximum Flow Rate'),
              })
            : undefined,
      }),
    })
    .refine((data) => data.minFlowRate < data.maxFlowRate, {
      message: translate.phrases.validation('{{label}} must be > {{value}}', {
        label: translate.phrases.banyanApp('Maximum Flow Rate'),
        value: translate.phrases.banyanApp('Minimum Flow Rate'),
      }),
      path: ['maxFlowRate'],
    })

  const form = useForm({
    validate: zodResolver(schema),
    validateInputOnBlur: true,
  })

  useEffect(() => {
    form.setValues({
      minFlowRate: defaultMinFlowRate,
      maxFlowRate: defaultMaxFlowRate,
    })
  }, [defaultMinFlowRate, defaultMaxFlowRate])

  const formUnchanged =
    form.values.minFlowRate === defaultMinFlowRate && form.values.maxFlowRate === defaultMaxFlowRate

  const updateButtonDisabled = !form.isValid() || formUnchanged

  const handleResetAndClose = () => {
    detailsPanelStore.setState((s) => ({
      ...s,
      flowMeterLimitsSettingsOpened: false,
    }))

    form.setValues({
      minFlowRate: defaultMinFlowRate,
      maxFlowRate: defaultMaxFlowRate,
    })
  }

  const handleCloseWithoutSaving = () => {
    if (!formUnchanged) {
      setConfirmModalOpened(true)
    } else {
      handleResetAndClose()
    }
  }

  const handleUpdate = async () => {
    if (!form.isValid() || !propertyId || !lngLat) {
      return
    }

    try {
      const convertedMinFlowRateGpm = convertCurrentFlowRateToGpm(form.values.minFlowRate)
      const convertedMaxFlowRateGpm = convertCurrentFlowRateToGpm(form.values.maxFlowRate)

      const response = await apiFetch({
        url: '/field-asset-settings-set',
        body: {
          irrigationFlowMeterSettingsUpdate: [
            {
              lngLat,
              propertyId,
              minFlowRateGpm: convertedMinFlowRateGpm,
              maxFlowRateGpm: convertedMaxFlowRateGpm,
            },
          ],
        },
      })

      if (response.irrigationFlowMeterSettingsUpdate === true) {
        showNotification({
          message: translate.phrases.banyanApp('Successfully updated flow meter limits'),
          type: 'success',
        })

        handleAfterSave(convertedMinFlowRateGpm, convertedMaxFlowRateGpm)

        detailsPanelStore.setState((s) => ({
          ...s,
          flowMeterLimitsSettingsOpened: false,
        }))
      } else {
        showNotification({
          message: translate.phrases.banyanApp('Failed to update flow meter limits'),
          type: 'error',
        })
      }
    } catch (error) {
      showNotification({
        message: translate.phrases.banyanApp('Failed to update flow meter limits'),
        type: 'error',
      })
    }
  }

  const handleDelete = async () => {
    if (!propertyId || !lngLat) {
      return
    }

    try {
      const response = await apiFetch({
        url: '/field-asset-settings-set',
        body: {
          irrigationFlowMeterSettingsUpdate: [
            {
              lngLat,
              propertyId,
              isDeleteLimits: true,
            },
          ],
        },
      })

      if (response.irrigationFlowMeterSettingsUpdate === true) {
        showNotification({
          message: translate.phrases.banyanApp('Successfully deleted flow meter limits'),
          type: 'success',
        })

        handleAfterSave(null, null)

        handleCloseWithoutSaving()
      } else {
        showNotification({
          message: translate.phrases.banyanApp('Failed to delete flow meter limits'),
          type: 'error',
        })
      }
    } catch (error) {
      showNotification({
        message: translate.phrases.banyanApp('Failed to delete flow meter limits'),
        type: 'error',
      })
    }
  }

  const onDeleteButtonPress = () => {
    showConfirmSettingModal({
      title: translate.phrases.banyanApp('Delete flow meter limits'),
      content: (
        <div>
          <p>
            {translate.phrases.banyanApp('Are you sure you want to delete this flow meter limits?', {
              alertName: name,
            })}
          </p>
        </div>
      ),
      onConfirm: handleDelete,
      confirmButtonText: translate.phrases.banyanApp('Delete limits'),
    })
  }

  return (
    <>
      <ModalDrawer
        title={translate.phrases.banyanApp('Set Flow Meter Limits')}
        opened={flowMeterLimitsSettingsOpened}
        primaryButtonText={translate.phrases.banyanApp('Apply')}
        primaryButtonOnPress={handleUpdate}
        primaryButtonDisabled={updateButtonDisabled}
        secondaryButtonText={translate.phrases.banyanApp('Delete')}
        secondaryButtonOnPress={onDeleteButtonPress}
        secondaryButtonDisabled={defaultMinFlowRate === '' && defaultMaxFlowRate === ''}
        onClose={handleCloseWithoutSaving}
        zIndex={SharedSettings.DEFAULT_MODAL_DRAWER_Z_INDEX}
      >
        <div css={{ display: 'flex', gap: 20, flexDirection: 'column', padding: '20px' }}>
          <div css={{ color: colors.grey800 }}>
            <NameWithExternalVendorIcons name={name} externalVendorNames={externalVendorNames} />
          </div>
          <form onSubmit={form.onSubmit(() => undefined)}>
            <div css={{ display: 'flex', gap: 20, flexDirection: 'column' }}>
              <div>
                <NumberInputWithUnit
                  label={translate.phrases.banyanApp('Minimum Flow Rate')}
                  unit={unitConverter.flowRate().suffix()}
                  {...form.getInputProps('minFlowRate')}
                  value={form.values?.minFlowRate}
                  onChange={(newValue) => {
                    form.setFieldValue('minFlowRate', newValue)
                  }}
                  error={''}
                  onBlur={form.validate}
                />
                {form.errors.minFlowRate && <ErrorText>{form.errors.minFlowRate}</ErrorText>}
              </div>
              <div>
                <NumberInputWithUnit
                  label={translate.phrases.banyanApp('Maximum Flow Rate')}
                  unit={unitConverter.flowRate().suffix()}
                  {...form.getInputProps('maxFlowRate')}
                  value={form.values?.maxFlowRate}
                  onChange={(newValue) => {
                    form.setFieldValue('maxFlowRate', newValue)
                  }}
                  error={''}
                  onBlur={form.validate}
                />
                {form.errors.maxFlowRate && <ErrorText>{form.errors.maxFlowRate}</ErrorText>}
              </div>
            </div>
          </form>
        </div>
      </ModalDrawer>
      <ConfirmationSettingsModal
        confirmModalOpened={confirmModalOpened}
        setConfirmModalOpened={setConfirmModalOpened}
        handleResetAndClose={handleResetAndClose}
        handleUpdate={handleUpdate}
      />
    </>
  )
}
